import {Text} from 'platform/foundation';
import styled, {css} from 'styled-components';

import {Fragment} from 'react';

import {
  OnlineOfferAlternative,
  OnlineOfferAlternativesResponseBody,
} from '../../../api/types/onlineOfferAlternativesTypes';
import {
  ComparisonGroup,
  ComparisonRow,
  isCellDifferentFromReference,
} from '../../../utils/buildComparisonRows';
import {getComparisonRowLabel} from '../../../utils/getComparisonRowLabel';
import {translateWithFallback as t} from '../../../utils/translateWithFallback';
import {useFormatComparisonValue} from '../hooks/useFormatComparisonValue';
import {ComparisonColumnHeader} from './ComparisonColumnHeader';

const GROUPS: {id: ComparisonGroup; labelKey: string; fallback: string}[] = [
  {
    id: 'price',
    labelKey: 'alternatives.comparison.groups.price',
    fallback: 'Price breakdown · incl. VAT',
  },
  {
    id: 'vehicle',
    labelKey: 'alternatives.comparison.groups.vehicle',
    fallback: 'Vehicle & delivery',
  },
  {
    id: 'equipment',
    labelKey: 'alternatives.comparison.groups.equipment',
    fallback: 'Equipment · standard, options and packages',
  },
];

interface ComparisonTableProps {
  alternatives: OnlineOfferAlternative[];
  rows: ComparisonRow[];
  reference: OnlineOfferAlternative;
  offerAlternatives: OnlineOfferAlternativesResponseBody;
  onSelect: (alternative: OnlineOfferAlternative) => void;
  onView?: (alternative: OnlineOfferAlternative) => void;
}

export function ComparisonTable(props: ComparisonTableProps) {
  const formatValue = useFormatComparisonValue();
  const referenceIndex = props.alternatives.findIndex(
    (alternative) => alternative.id === props.reference.id
  );
  const chosenId = props.offerAlternatives.customerChoice?.alternativeId;

  return (
    <StyledScrollArea data-testid="alternativesComparisonTable">
      <StyledTable $columns={props.alternatives.length}>
        <caption>
          {t(
            'alternatives.comparison.caption',
            'Offered vehicle alternatives, prices and equipment differences'
          )}
        </caption>
        <thead>
          <tr>
            <th scope="col">
              <Text size="small" alternative>
                {t('alternatives.strip.caption', 'Your alternatives')}
              </Text>
              <Text size="xSmall" color="secondary">
                {t('alternatives.comparison.vatNote', 'All total prices include VAT.')}
              </Text>
              <Text size="xSmall" color="secondary">
                {t(
                  'alternatives.comparison.highlightNote',
                  'Highlighted cells differ from your reference.'
                )}
              </Text>
            </th>
            {props.alternatives.map((alternative) => (
              <StyledColumnHeader
                key={alternative.id}
                scope="col"
                $isChosen={alternative.id === chosenId}
                data-testid={`alternativeColumn-${alternative.letter}`}
              >
                <ComparisonColumnHeader
                  alternative={alternative}
                  reference={props.reference}
                  offerAlternatives={props.offerAlternatives}
                  onSelect={props.onSelect}
                  onView={props.onView}
                />
              </StyledColumnHeader>
            ))}
          </tr>
        </thead>
        <tbody>
          {GROUPS.map((group) => {
            const groupRows = props.rows.filter((row) => row.group === group.id);
            if (groupRows.length === 0) {
              return null;
            }

            return (
              <Fragment key={group.id}>
                <StyledGroupRow>
                  <th scope="rowgroup" colSpan={props.alternatives.length + 1}>
                    <Text size="small" alternative>
                      {t(group.labelKey, group.fallback)}
                    </Text>
                  </th>
                </StyledGroupRow>
                {groupRows.map((row) => (
                  <tr key={row.id} data-testid={`comparisonRow-${row.id}`}>
                    <th scope="row">
                      <Text size="small" alternative>
                        {getComparisonRowLabel(row)}
                      </Text>
                    </th>
                    {row.values.map((value, index) => (
                      <StyledCell
                        key={props.alternatives.at(index)?.id}
                        $isHighlighted={isCellDifferentFromReference(row, index, referenceIndex)}
                      >
                        <Text size="small">{formatValue(value)}</Text>
                      </StyledCell>
                    ))}
                  </tr>
                ))}
              </Fragment>
            );
          })}
        </tbody>
      </StyledTable>
    </StyledScrollArea>
  );
}

// The area scrolls both ways so the header row and the label column can stay sticky inside it.
const StyledScrollArea = styled.div`
  overflow: auto;
  max-height: 80vh;
  border: 1px solid ${({theme}) => theme.colors.border.emphasisDefault};
  border-radius: ${({theme}) => theme.radii.medium};
`;

const StyledTable = styled.table<{$columns: number}>`
  width: 100%;
  min-width: ${({theme, $columns}) =>
    `calc(${theme.getSize(55)} + ${$columns} * ${theme.getSize(60)})`};
  border-collapse: separate;
  border-spacing: 0;
  table-layout: fixed;

  caption {
    position: absolute;
    width: 1px;
    height: 1px;
    overflow: hidden;
    clip: rect(0 0 0 0);
  }

  th,
  td {
    padding: ${({theme}) => theme.getSize(4)};
    border-bottom: 1px solid ${({theme}) => theme.colors.border.emphasisDefault};
    text-align: left;
    vertical-align: top;
    background-color: ${({theme}) => theme.colors.fill.white};
  }

  /* First column: row labels, sticky while scrolling horizontally. */
  th:first-child {
    position: sticky;
    left: 0;
    z-index: 1;
    width: ${({theme}) => theme.getSize(55)};
    background-color: ${({theme}) => theme.colors.semantic.bg.subtle};
  }

  /* Header row: alternative columns, sticky while scrolling vertically. */
  thead th {
    position: sticky;
    top: 0;
    z-index: 2;
  }

  thead th:first-child {
    z-index: 3;
    vertical-align: bottom;
  }

  @media ${({theme}) => theme.devices.mobile} {
    min-width: ${({theme, $columns}) =>
      `calc(${theme.getSize(30)} + ${$columns} * ${theme.getSize(55)})`};

    th:first-child {
      width: ${({theme}) => theme.getSize(30)};
    }
  }
`;

const StyledColumnHeader = styled.th<{$isChosen: boolean}>`
  /* A definite height lets the header content fill the row (the table still grows it to fit). */
  height: 1px;
  ${({$isChosen, theme}) =>
    $isChosen &&
    css`
      box-shadow: inset 0 ${theme.getSize(1)} 0 ${theme.colors.accent.base};
    `}
`;

const StyledGroupRow = styled.tr`
  th {
    background-color: ${({theme}) => theme.colors.semantic.bg.subtle};
  }
`;

const StyledCell = styled.td<{$isHighlighted: boolean}>`
  ${({$isHighlighted, theme}) =>
    $isHighlighted &&
    css`
      /* Doubled class beats the table's own cell background. */
      && {
        background-color: ${theme.colors.accent.lightest};
      }
    `}
`;
